import type { Capability } from '../model/capabilities';

/**
 * The account-level `role` claim value for a platform super admin. Mirrors the
 * backend `Role.Admin` string (and the legacy `NewAuthService.isAdmin()`
 * check). Super admins bypass capability gating in the UI, matching the
 * server-side `CapabilitiesGuard` bypass — UI parity only, never enforcement.
 */
export const SUPER_ADMIN_ROLE = 'superAdmin';

/**
 * The partner-access summary carried in the `roles_token` for one partner the
 * user belongs to. This is the **coarse altitude** of the two-altitudes model
 * (see design doc §6): it tells the UI whether to surface the "Manage partner
 * area" entry point and how to gate coarse navigation.
 *
 * It is a **UI hint only**. The `caps` here can lag behind a seat/role
 * revocation, so anything that actually gates a capability must read the fresh
 * `GET /v1/me/capabilities` via `CapabilitiesService` (fp1-04), and the backend
 * `CapabilitiesGuard` remains the real enforcement.
 */
export interface PartnerSummary {
  partnerId: string;
  caps: Capability[];
  /**
   * The seat is paused (HAL-143) — carried so `requirePartnerAccess` lets the
   * person into the shell and `MembershipGate` can tell them *why* they have
   * nothing, instead of the coarse gate bouncing them to a bare 403.
   *
   * **Not an authorization signal, in either direction.** `caps` is already
   * empty here and that is what gates; this only picks the copy. Absent for an
   * active seat rather than `false`, so the common entry stays byte-identical
   * to what the backend minted before.
   */
  suspended?: true;
}

/**
 * Decoded shape of the non-httpOnly `roles_token` cookie. Ported from the
 * legacy `cookie.service.ts` `RolesTokenPayload` and extended with
 * `partnerAccess` (backend `p1-05`).
 *
 * The httpOnly `access_token` / `refresh_token` cookies are backend-only and
 * are never decoded here.
 */
export interface RolesTokenPayload {
  role: string;
  sub: string;
  stripeStatus?: string;
  isOnboarding?: boolean;
  /** Per-partner access summary — present once backend `p1-05` mints it. */
  partnerAccess?: PartnerSummary[];
  /** Expiry, in seconds since the epoch (JWT convention). */
  exp: number;
  /** Issued-at, in seconds since the epoch. */
  iat: number;
}

/**
 * The lightweight session identity derived from `roles_token` claims. This is
 * **not** the full user profile — fetch that from the API when a surface needs
 * it. `SessionService` exposes only what the token can answer cheaply.
 */
export interface SessionUser {
  id: string;
  role: string;
  isOnboarding: boolean;
  stripeStatus: string | null;
}
